import type { LoggedMeal } from "./types";
import type { WeightLog } from "./weight";

const KCAL_PER_LB_FAT = 3500;
const LBS_PER_KG = 2.2046226218;

export interface TDEEResult {
  tdee_kcal: number;
  days: number;
  avg_intake: number;
  weight_change_lbs: number;
  first_date: string;
  last_date: string;
}

/**
 * Estimate TDEE from intake vs. weight change. Requires at least 2 weight
 * entries spanning at least 7 days and meals logged in the same period.
 *
 * TDEE = avg_daily_intake - (weight_change_lbs * 3500 / days)
 *   - positive weight change → intake exceeded TDEE
 *   - negative weight change → intake was below TDEE
 */
export function computeTDEE(
  meals: LoggedMeal[],
  weights: WeightLog[]
): TDEEResult | null {
  if (weights.length < 2) return null;

  const sorted = [...weights].sort((a, b) => a.logged_at.localeCompare(b.logged_at));
  const first = sorted[0];
  const last = sorted[sorted.length - 1];

  const firstDate = new Date(first.logged_at);
  const lastDate = new Date(last.logged_at);
  const days = (lastDate.getTime() - firstDate.getTime()) / (24 * 60 * 60 * 1000);
  if (days < 7) return null;

  const weightChangeKg = last.weight_kg - first.weight_kg;
  const weightChangeLbs = weightChangeKg * LBS_PER_KG;

  const totalKcal = meals
    .filter((m) => {
      const d = new Date(m.logged_at);
      return d >= firstDate && d <= lastDate;
    })
    .reduce((s, m) => s + m.estimate.totals.calories, 0);

  if (totalKcal === 0) return null;

  const avgIntake = totalKcal / days;
  const tdee = avgIntake - (weightChangeLbs * KCAL_PER_LB_FAT) / days;

  return {
    tdee_kcal: Math.round(tdee),
    days: Math.round(days),
    avg_intake: Math.round(avgIntake),
    weight_change_lbs: Math.round(weightChangeLbs * 10) / 10,
    first_date: first.logged_at,
    last_date: last.logged_at
  };
}
